import React, { ReactElement, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import { OutletContext } from '../../layout/Layout';
import {
	AlertVariant,
	Button,
	Modal,
	ModalBoxFooter,
	ModalVariant,
	TextInput,
	Text,
	Alert,
	NumberInput,
	TextArea,
} from '@patternfly/react-core';
import ZiTable, { Action, Column } from '../../components/table/ZiTable';
import FilterTableLayout from '../../layout/FilterTableLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib/components/toast/ToastProvider';
import { Permission } from '../../enums/permission.enum';
import PermissionButton from '../../components/button/PermissionButton';
import { faSpinner, faTrashAlt, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { QuestionCategory, TQuestionCategory } from '../../api/question-category/QuestionCategory';
import IconCardDropdown from '../../helpers/helper-components/IconCardDropdown';
import {
	faDiploma,
	faFileCertificate,
	faAward,
	faDollarSign,
	faPeopleGroup,
	faBuildingColumns,
	faListCheck,
	faClipboardListCheck,
	faBarsProgress,
	faCalendarDays,
	faCalendarImage,
	faPeopleArrows,
	faSitemap,
	faBuildingUser,
	faBuilding,
	faTransporter,
	faSparkles,
	faChartUser,
	faChartNetwork,
	faShieldCheck,
	faUserHelmetSafety,
	faMoneyCheckDollar,
	faFolderUser,
	faPersonArrowDownToLine,
	faPersonArrowUpFromLine,
	faClockDesk,
	faCalendarClock,
	faUserClock,
	faTruckClock,
	faHourglassClock,
	faPause,
	faGift,
	faHandshake,
	faTemperatureList,
	faLayerGroup,
	faChartArea,
	faChartSimpleHorizontal,
	faFilter,
	faGauge,
	faHeat,
	faChartLineUp,
	faChartPie,
	faCircleDot,
	faDiagramSankey,
	faChartScatter,
	faChartScatterBubble,
	faTable,
	faCloudWord,
	faChartSimple,
	faLollipop,
} from '@fortawesome/pro-regular-svg-icons';
import * as RegularIcons from '@fortawesome/pro-regular-svg-icons';

export default function QuestionCategories(): ReactElement {
	const { addToast } = useToast();
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
	const [data, setData] = useState<TQuestionCategory[]>([]);
	const [tableLoading, setTableLoading] = useState<boolean>(true);
	const [activeQuestionCategory, setActiveQuestionCategory] = useState<TQuestionCategory>(
		QuestionCategory.Default() as QuestionCategory
	);
	const [categoryText, setcategoryText] = useState('');
	const [categorySequence, setCategorySequence] = useState(0);
	const [categoryDescription, setCategoryDescription] = useState('');
	const [categoryId, setCategoryId] = useState<number>();
	const [validationMessage, setValidationMessage] = useState<React.ReactElement | null>();
	const [_isExpanded, setIsExpanded] = useState(false);
	const [selectedCategoryIcon, setSelectedCategoryicon] = useState<IconDefinition>(faLayerGroup);
	const [isLoading, setIsLoading] = useState(false);
	const iconNames = [
		faDiploma,
		faFileCertificate,
		faAward,
		faDollarSign,
		faPeopleGroup,
		faBuildingColumns,
		faListCheck,
		faClipboardListCheck,
		faBarsProgress,
		faCalendarDays,
		faCalendarImage,
		faPeopleArrows,
		faSitemap,
		faBuildingUser,
		faBuilding,
		faTransporter,
		faSparkles,
		faChartUser,
		faChartNetwork,
		faShieldCheck,
		faUserHelmetSafety,
		faMoneyCheckDollar,
		faFolderUser,
		faPersonArrowDownToLine,
		faPersonArrowUpFromLine,
		faClockDesk,
		faCalendarClock,
		faUserClock,
		faTruckClock,
		faHourglassClock,
		faPause,
		faGift,
		faHandshake,
		faTemperatureList,
		faLayerGroup,
		faChartArea,
		faChartSimpleHorizontal,
		faFilter,
		faGauge,
		faHeat,
		faChartLineUp,
		faChartPie,
		faCircleDot,
		faDiagramSankey,
		faChartScatter,
		faChartScatterBubble,
		faTable,
		faCloudWord,
		faChartSimple,
		faLollipop,
	];

	function getRegularIcon(iconName: string): IconDefinition | undefined {
		// Convert 'dollar-sign' → 'faDollarSign'
		const pascalCase =
			'fa' +
			iconName
				.split('-')
				.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
				.join('');

		// Use type assertion to satisfy TS
		return (RegularIcons as unknown as Record<string, IconDefinition>)[pascalCase];
	}

	const selectedColumns: Column<TQuestionCategory>[] = [
		{
			title: 'Name',
			columnName: 'title',
			sortable: true,
		},
		{
			title: 'icon',
			columnName: 'icon',
			sortable: true,
			customAccessor(row) {
				const iconDef = getRegularIcon(row.icon);
				return iconDef ? (
					<FontAwesomeIcon
						size="2x"
						icon={iconDef}
					/>
				) : (
					<span>—</span>
				);
			},
		},
		{
			title: 'Sequence',
			columnName: 'sequence',
			sortable: true,
		},
	];

	const actions: Action<QuestionCategory>[] = [
		{
			name: (
				<>
					<FontAwesomeIcon icon={faPenToSquare} />
					Edit
				</>
			),
			callback: (item) => {
				setActiveQuestionCategory(item);
				setIsModalOpen(true);
			},
			permission: Permission.EditQuestionCategory,
		},
		{
			name: (
				<>
					<FontAwesomeIcon icon={faTrashAlt} />
					Delete
				</>
			),
			callback: (item) => {
				setTableLoading(true);
				QuestionCategory.Delete(item.id!)
					.then((response) => {
						if (response) {
							addToast('Question Category succesfully deleted', AlertVariant.success);
							getQuestionCategories();
						} else {
							setTableLoading(false);
						}
					})
					.catch(() => {
						setTableLoading(false);
						addToast(
							'An error occurred while trying to delete Question Category. Please try again later.',
							AlertVariant.danger
						);
					});
			},
			permission: Permission.DeleteQuestionCategory,
		},
	];

	useEffect(() => {
		setcategoryText(activeQuestionCategory.title);
		setCategorySequence(activeQuestionCategory.sequence ?? 0);
		const iconDef = getRegularIcon(activeQuestionCategory.icon ?? 'faLayerGroup');
		if (iconDef) setSelectedCategoryicon(iconDef);
		setCategoryDescription(activeQuestionCategory.description);
		setCategoryId(activeQuestionCategory.id);
	}, [activeQuestionCategory]);

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle="Question Categories"
					pageDescription="Manage your Question Categories."
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
				/>
			),
		});

		getQuestionCategories();
	}, [setSubSide, subNavExpanded, setSubNavExpanded, isModalOpen]);

	const getQuestionCategories = () => {
		QuestionCategory.GetAll()
			.then((data) => {
				setData(
					data.sort((a, b) => {
						const seqDiff = (a.sequence ?? 0) - (b.sequence ?? 0);
						if (seqDiff !== 0) return seqDiff;

						// Secondary sort: title ascending
						return a.title.localeCompare(b.title);
					})
				);
				setTableLoading(false);
			})
			.catch(() => {
				addToast(
					'An error occurred while trying to load key measures. Please try again later.',
					AlertVariant.danger
				);
			});
	};

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const handleSuccess = () => {
		getQuestionCategories();
		setIsModalOpen(false);
	};

	const addButton = (
		<PermissionButton
			data-testid={'question-cat-add-btn'}
			variant={'primary'}
			onClick={() => {
				setActiveQuestionCategory(QuestionCategory.Default() as QuestionCategory);
				setIsModalOpen(true);
			}}
			permission={Permission.CreateQuestionCategory}
		>
			New Question Category
		</PermissionButton>
	);

	const questionCatTable = (
		<ZiTable<QuestionCategory>
			ariaLabel={'Question Category table'}
			columns={selectedColumns}
			data={data}
			caption="Question Categories"
			actions={actions}
			loading={tableLoading}
		/>
	);

	const handleTextChange = (value: string, _event: React.FormEvent<HTMLInputElement>) => {
		setcategoryText(value);
	};

	//Update actionText when the modal is opened
	useEffect(() => {
		if (!isModalOpen) {
			setValidationMessage(null);
			setcategoryText('');
			setSelectedCategoryicon(faLayerGroup);
			setCategoryId(undefined);
		}
	}, [isModalOpen]);

	const submitAddQuestionCat = () => {
		if (categoryText === '') {
			setValidationMessage(
				<Alert
					variant="danger"
					isInline
					isPlain
					title={`Category Text is required`}
				/>
			);

			return;
		} else {
			SaveAddEditQuestionCat();
			handleClose();
		}
	};

	const SaveAddEditQuestionCat = () => {
		const questionCatRequest: TQuestionCategory = {
			description: categoryDescription,
			title: categoryText,
			sequence: categorySequence,
			icon: selectedCategoryIcon.iconName,
		};

		if (categoryId) {
			questionCatRequest.id = categoryId;
			void QuestionCategory.Update(questionCatRequest).then(() => {
				handleSuccess();
				addToast('Question Category succesfully updated', AlertVariant.success);
			});
		} else {
			void QuestionCategory.New(questionCatRequest).then(() => {
				handleSuccess();
				addToast('Question Category succesfully added', AlertVariant.success);
			});
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			const classes = target.classList;
			const isDropdownItemClicked =
				classes.contains('pf-c-dropdown__menu-item') ||
				classes.contains('pf-c-dropdown__toggle');
			if (!isDropdownItemClicked) {
				setIsExpanded(false);
			}
		};

		document.addEventListener('click', handleClickOutside);

		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, []);

	const onSequenceChange = (event: React.FormEvent<HTMLInputElement>, isPrevious: boolean) => {
		const value = (event.target as HTMLInputElement).value;
		setCategorySequence(+value);
	};

	return (
		<React.Fragment>
			<Modal
				variant={ModalVariant.large}
				title="Question Category Management"
				isOpen={isModalOpen}
				onClose={handleClose}
			>
				{validationMessage && (
					<div>
						<br />
						{validationMessage}
					</div>
				)}
				<span className="spacer-sm"></span>
				<Text>
					Question Category Name <span className="pf-m-danger">*</span>
				</Text>
				<TextInput
					value={categoryText}
					onChange={handleTextChange}
				/>
				<br />
				<br />
				<Text>Sequence</Text>
				<NumberInput
					type="number"
					id="future_periods"
					name="tbQuestionCatSequence"
					value={categorySequence}
					onChange={(e: React.FormEvent<HTMLInputElement>) => {
						onSequenceChange(e, false);
					}}
					onMinus={() => {
						setCategorySequence(categorySequence - 1);
					}}
					onPlus={() => {
						setCategorySequence(categorySequence + 1);
					}}
				/>
				<br />
				<br />
				<Text>Category icon</Text>
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<IconCardDropdown
						iconElements={iconNames ?? []}
						setSelectedCategoryicon={setSelectedCategoryicon}
						selectedIcon={selectedCategoryIcon}
						setIsLoading={setIsLoading}
					/>

					{isLoading && (
						<FontAwesomeIcon
							icon={faSpinner}
							spin={true}
							size="2x"
						/>
					)}
				</div>
				<br />
				<br />
				<Text>Description</Text>
				<TextArea
					value={categoryDescription}
					onChange={(value) => {
						setCategoryDescription(value);
					}}
				/>

				<div>
					<ModalBoxFooter className="pull-right add-question-footer">
						<Button
							variant="secondary"
							onClick={() => {
								handleClose();
							}}
						>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={submitAddQuestionCat}
						>
							Submit
						</Button>
					</ModalBoxFooter>
				</div>
			</Modal>
			<React.Fragment>
				<FilterTableLayout
					table={questionCatTable}
					layoutActions={addButton}
				/>
			</React.Fragment>
		</React.Fragment>
	);
}
